import React, { useMemo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import "./common.css";

const CustomStackedTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="custom-tooltip">
        {payload.map((entry) => (
          <div key={entry.name ?? entry.dataKey} className="tooltip-row">
            <div className="tooltip-left">
              <span
                className="tooltip-dot"
                style={{ backgroundColor: entry.color }}
              />
              <span className="tooltip-label">
                {entry.name}
              </span>
            </div>
            <span className="tooltip-value">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CommonStackedScrollableChart = ({
  data = [],
  xKey = "week",
  stackBars = [],
  height = 340,
}) => {


  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);


  const dynamicWidth = useMemo(() => {
    const calculatedWidth = data.length * 120;
    return Math.max(calculatedWidth, containerWidth);
  }, [data, containerWidth]);

  const [activeKeys, setActiveKeys] = useState(
    stackBars.map((b) => b.dataKey)
  );

  const toggleBar = (key) => {
    setActiveKeys((prev) =>
      prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key]
    );
  };

  return (
    <div className="scroll-chart-container" ref={containerRef}>

      <div className="chart-scroll-area">
        <div
          className="chart-inner-dynamic"
          style={{ width: dynamicWidth }}
        >
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={data}
              barCategoryGap="15%"
              barGap={0}
              margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />

              <XAxis 
                dataKey={xKey}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#667f93", fontSize: 12, fontFamily: "'UnileverShilling', sans-serif" }}
              />

              <YAxis hide domain={[0, 110]} />

              <Tooltip content={<CustomStackedTooltip />} />

              {stackBars
                .filter((bar) => activeKeys.includes(bar.dataKey))
                .map((bar, index, filteredBars) => {

                  const isTop = 
                    index === filteredBars.length - 1;

                  return (
                    <Bar
                      key={bar.dataKey}
                      dataKey={bar.dataKey}
                      stackId="a"
                      fill={bar.color}
                      barSize={40}
                      radius={
                        isTop ? [10, 10, 0, 0] : [0, 0, 0, 0]
                      }
                    >
                      <LabelList
                        dataKey={bar.dataKey}
                        position="inside"
                        formatter={(value) =>
                          value ? `${value}%` : ""
                        }
                        style={{
                          fontSize: 12,
                          fill: bar.color === "#0066cc" ? "#fff" : "#1a2025",
                          fontWeight: 600,
                        }}
                      />
                    </Bar>
                  );
                })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 🔥 Clickable Legend */}
      <div className="custom-legends">
        {stackBars.map((item) => {
          const isActive = activeKeys.includes(item.dataKey);

          return (
            <button
              type="button"
              key={item.dataKey}
              className="legend-items"
              aria-pressed={isActive}
              onClick={() => toggleBar(item.dataKey)}
              style={{ opacity: isActive ? 1 : 0.4 }}
            >
              <span
                className="legend-colors"
                style={{ backgroundColor: item.color }}
              />
              <span className="legend-texts">
                {item.label || item.dataKey}
              </span>
            </button>
          );
        })}
      </div>

    </div>
  );
};

CustomStackedTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      dataKey: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string,
    }),
  ),
};

CommonStackedScrollableChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  xKey: PropTypes.string,
  stackBars: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  height: PropTypes.number,
};

export default CommonStackedScrollableChart;
